import { Fragment, useEffect, useState, type ReactNode } from 'react';

import { MAX_EVENTS, MAX_TOPICS, type TimelineResponse } from '@lifetimeline/shared';

import { Button } from './Button.js';
import { CheckIcon, InfinityIcon } from './icons.js';
import { Sheet } from './Sheet.js';
import { useI18n } from '../i18n/index.js';
import { api } from '../lib/apiClient.js';
import styles from './UpgradeDialog.module.css';

/**
 * 플랜 표.
 *
 * 무료로 되는 데까지 온 사람에게 뜬다 — 상한을 넘겨 담으려 할 때도, 누가
 * 봤는지 보려 할 때도 **같은 모달**이다. 파는 물건이 하나라서다.
 *
 * **화면을 옮기지 않는다.** 하던 일 위에 떠야 한다 — 다른 화면으로 보냈다가
 * 돌아오면 하려던 일을 다시 찾아야 한다.
 *
 * 예전에는 "준비 중이에요" 한 줄이었다. 벽에 부딪힌 사람이 제일 먼저 궁금한
 * 것은 **내가 얼마나 썼고 얼마까지 되는가**인데, 그 줄은 아무 답도 안 했다.
 *
 * ⚠️ **값을 적지 않는다.** 결제사가 붙기 전까지는 얼마인지 말하지 않는다 —
 * 살 수 없는 것에 값을 매겨 두면 사고 싶어도 살 방법이 없는 사람에게 벽만
 * 보여주는 셈이다.
 */

/**
 * 플러스 칸을 내보낼지.
 *
 * ⚠️ **지금은 꺼 뒀다**(2026-10). 사람을 모으는 게 먼저라 유료 칸막이를
 * 걷고 상한을 넉넉히 올렸다 — 파는 것이 없는데 "플러스" 칸을 보여주면
 * 없는 물건을 광고하는 꼴이다.
 *
 * 표의 뼈대는 그대로라 켜는 데 다른 손질이 필요 없다. 이 모달 자체는
 * **지우지 않는다** — 상한은 사라진 게 아니라 넉넉해진 것이라, 300개를
 * 채운 사람에게는 여전히 떠야 한다.
 */
const SHOW_PLUS = false;

/**
 * 플러스의 주제 상한.
 *
 * 기록은 무제한인데 **주제는 아니다** — 주제는 타임라인의 축을 가르는
 * 것이라, 수가 늘면 담는 사람이 아니라 **보는 사람**이 읽기 어려워진다.
 * 기록은 쌓일수록 좋지만 축은 그렇지 않다.
 *
 * ⚠️ **아직 정해지지 않은 숫자다.** 기본 쪽(`MAX_TOPICS`)은 서버가 실제로
 * 막는 값이라 `shared`에 있다. 이쪽은 아무 데서도 안 쓰인다 — 화면에
 * 보여주려고 여기 둔 것뿐이다. 정해지면 여기만 고치고, 서버가 막기
 * 시작할 때 `shared`로 옮긴다.
 */
const PLUS_MAX_TOPICS = 20;

interface Usage {
  topics: number;
  events: number;
}

export function UpgradeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const [usage, setUsage] = useState<Usage | null>(null);

  /*
   * 쓰는 수는 **열릴 때 한 번 읽는다.**
   *
   * 한 쪽만 받아도 된다 — 서버가 `eventTotal`로 전체 개수를 같이 준다.
   * 예전에는 그 값이 없어서 쪽을 끝까지 넘겨 가며 셌다.
   *
   * 부르는 쪽에서 들고 와 넘기게 하면 두 군데(타임라인·분석)가 각자 세야
   * 하는데, 분석 화면은 애초에 기록을 안 읽는다. 모달이 자기 것을 자기가 읽는다.
   */
  useEffect(() => {
    if (!open) {
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const data = await api.get<TimelineResponse>('/api/me/timeline');
        if (alive) {
          /* 첫 쪽만 읽으므로 `eventTotal`은 반드시 들어 있다 */
          setUsage({ topics: data.topics.length, events: data.eventTotal ?? 0 });
        }
      } catch {
        /* 못 읽어도 표는 뜬다 — 상한은 알고 있고, 쓰는 수만 빈칸이 된다 */
      }
    })();
    return () => {
      alive = false;
    };
  }, [open]);

  return (
    <Sheet open={open} onClose={onClose} title={t('upgrade.title')}>
      <div className={`${styles.table} ${SHOW_PLUS ? '' : styles.twoColumns}`}>
        {/*
          ⚠️ 머리에 "쓰는 중"·"준비 중"을 붙여 봤다가 뺐다. 플랜 이름 밑에
          상태가 또 붙으니 칸마다 읽을 것이 둘이 됐고, "준비 중"은 표 아래
          같은 말이 또 있어 두 번 읽혔다. **지금 선 칸은 밑선이 또렷한 것**
          으로, **못 사는 칸은 흐린 것**으로 말한다 — 글자를 안 늘린다.
        */}
        <span className={styles.headLabel} />
        <span className={`${styles.head} ${styles.headMine}`}>{t('upgrade.basic')}</span>
        {SHOW_PLUS ? <span className={styles.head}>{t('upgrade.plus')}</span> : null}

        <Row label={t('upgrade.topics')}>
          <Count now={usage?.topics} max={MAX_TOPICS} />
          <Count now={usage?.topics} max={PLUS_MAX_TOPICS} dim />
        </Row>
        <Row label={t('upgrade.records')}>
          <Count now={usage?.events} max={MAX_EVENTS} />
          <Unlimited />
        </Row>
        <Row label={t('share.card')}>
          <Check on />
          <Check on dim />
        </Row>
        <Row label={t('upgrade.connections')}>
          <Check on />
          <Check on dim />
        </Row>
        {/* 유료 칸막이를 걷으면서 기본도 누구인지까지 본다 */}
        <Row label={t('viewers.pageTitle')}>
          <Check on />
          <Check on dim />
        </Row>
      </div>

      <p className={styles.note}>{t('upgrade.soon')}</p>

      <Button fullWidth variant="outline" onClick={onClose}>
        {t('common.close')}
      </Button>
    </Sheet>
  );
}

/** 한 줄 = 칸 셋 */
function Row({ label, children }: { label: string; children: [ReactNode, ReactNode] }) {
  const [basic, premium] = children;
  return (
    <Fragment>
      <span className={styles.label}>{label}</span>
      <span className={styles.cell}>{basic}</span>
      {SHOW_PLUS ? <span className={styles.cell}>{premium}</span> : null}
    </Fragment>
  );
}

/**
 * 상한이 없다.
 *
 * 숫자를 지어내지 않아도 된 자리다 — 예전에는 여기에 20·300을 적어 뒀는데,
 * 아무 데서도 안 쓰이는 값이라 **정해지기 전까지는 거짓말**이었다.
 */
function Unlimited() {
  const { t } = useI18n();
  return (
    <span className={`${styles.check} ${styles.soon}`}>
      <InfinityIcon size={20} />
      <span className="srOnly">{t('upgrade.unlimited')}</span>
    </span>
  );
}

/**
 * `3/5`. 다 찼으면 숫자가 빨갛다 — 이 줄 때문에 모달이 떴다.
 *
 * **두 칸 다 쓰는 수를 붙인다.** 처음에는 플러스 쪽에 상한만 적었다 —
 * `3/20`이라고 쓰면 이미 그 플랜을 쓰는 것처럼 읽힐까 봐서다. 양쪽에 같은
 * 수가 붙으면 그 걱정이 사라지고, 대신 **바꾸면 여유가 이만큼**이 한눈에
 * 보인다. 비교하라고 나란히 둔 표다.
 */
function Count({
  now,
  max,
  dim = false,
}: {
  now?: number | undefined;
  max: number;
  dim?: boolean;
}) {
  if (now === undefined) {
    return <span className={styles.skeleton} aria-hidden="true" />;
  }
  /* 가득 찬 것은 **그 칸의 상한 기준**이다 — 기본에서 넘쳤어도 플러스에서는 안 넘친다 */
  const full = now >= max;
  return (
    <span
      className={`${styles.count} ${full ? styles.full : ''} ${dim && !full ? styles.soon : ''}`}
    >
      {now}/{max}
    </span>
  );
}

/**
 * 되는 것은 체크, 안 되는 것은 **비워 둔다.**
 *
 * ✕도 빗금도 안 쓴다 — ✕는 "틀렸다"로 읽히고, 빗금은 칸마다 기호가 둘이라
 * 눈이 체크를 세는 대신 둘을 구분하게 된다. 빈칸이면 체크만 세면 된다.
 */
function Check({ on, dim = false }: { on: boolean; dim?: boolean }) {
  const { t } = useI18n();
  if (!on) {
    return <span className="srOnly">{t('upgrade.notIncluded')}</span>;
  }
  return (
    <span className={`${styles.check} ${dim ? styles.soon : ''}`}>
      <CheckIcon size={18} />
      <span className="srOnly">{t('upgrade.included')}</span>
    </span>
  );
}
